import { create } from "zustand";
import { persist } from "zustand/middleware";
import { Capacitor } from "@capacitor/core";
import type { User } from "@/types/domain";
import { queryClient } from "@/lib/query-client";
import { unregisterPushDevice } from "@/services/push-device-api";

export interface ActiveOwner {
  id: string;
  name: string;
  email: string;
}

export interface ActiveProperty {
  id: string;
  name: string;
  type: string;
}

interface AppState {
  token: string | null;
  user: User | null;
  activeOwner: ActiveOwner | null;
  activeProperty: ActiveProperty | null;
  sidebarOpen: boolean;
  /** FCM registration token for this device (not persisted). */
  pushToken: string | null;
  setSession: (token: string, user: User) => void;
  setUser: (user: User) => void;
  clearSession: () => void;
  enterProperty: (owner: ActiveOwner | null, property: ActiveProperty) => void;
  exitWorkspace: () => void;
  setSidebarOpen: (open: boolean) => void;
  setPushToken: (token: string | null) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      activeOwner: null,
      activeProperty: null,
      sidebarOpen: false,
      pushToken: null,
      setSession: (token, user) => set((state) => {
        // A different account logging in must never see the previous account's cached
        // owners/properties/bookings - react-query keys for those lists aren't tenant-scoped.
        if (state.user && state.user.id !== user.id) queryClient.clear();
        return {
          token,
          user,
          activeOwner: state.user?.id === user.id ? state.activeOwner : null,
          activeProperty: state.user?.id === user.id ? state.activeProperty : null,
        };
      }),
      setUser: (user) => set({ user }),
      clearSession: () => {
        const { token, pushToken } = get();
        // Push delivery is server + OS driven, entirely independent of the in-app session - the
        // backend will keep sending this device FCM notifications (with sound) for the account's
        // bookings until the device is unregistered, so logout must do that itself. Fire it while
        // the token is still valid (before it's nulled below); swallow failures (already expired,
        // offline) so logout never gets stuck on it. `token &&` also stops this firing again on the
        // recursive clearSession() that api-client's own 401 handling triggers once state is cleared.
        if (token && pushToken && Capacitor.isNativePlatform()) {
          void unregisterPushDevice(pushToken).catch(() => undefined);
        }
        queryClient.clear();
        set({ token: null, user: null, activeOwner: null, activeProperty: null, pushToken: null });
      },
      enterProperty: (activeOwner, activeProperty) => set({ activeOwner, activeProperty }),
      exitWorkspace: () => set({ activeOwner: null, activeProperty: null }),
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      setPushToken: (pushToken) => set({ pushToken }),
    }),
    {
      name: "darshan-connector-session",
      partialize: ({ token, user, activeOwner, activeProperty }) => ({ token, user, activeOwner, activeProperty }),
    },
  ),
);
